import {
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Vector3,
  type BufferGeometry,
  type Material,
  type ShaderMaterial,
} from 'three';
import type { Rng } from '../../core/rng';
import type { Animated } from '../types';
import { edgeIdFor, vesselEdgeIdFor } from '../mesh/attributes';
import { layoutToWorld } from '../../core/coords';
import { WORLD } from '../../core/config';
import { SHIP_TEMPLATES, type ShipType } from './shipTemplates';
import { isFormed } from './shipForm';
import { isOpenBoat } from './openBoat';
import type { ShipTier } from './hullParts';
import { painterScaleOf, type PlacedShip } from './shipPlacement';
import {
  SMOKE_FROM,
  buildBodyGeometry,
  buildShipGeometry,
  createShipParts,
  boatColors,
  type ShipColors,
  type ShipParts,
} from './shipGeometry';
import { rigFor, type ShipRig } from './shipRig';
import { PAINTED_PENNANTS, pennantColourOf, type PennantColour } from './pennantColours';
import { generateMotionParams, type ShipInstance } from './shipMotion';
import { createFleetLod, type FleetBody, type FleetShip } from './fleetLod';
import { fleetClouds } from './smokeShapes';
import type { SurfaceMesh } from './surfaceNet';

export interface Fleet {
  readonly meshes: readonly InstancedMesh[];
  readonly animated: readonly Animated[];
}

interface Group {
  readonly key: string;
  /** The rig its geometry is built with. */
  readonly rig: ShipRig;
  /** The colour its pennants fly. */
  readonly pennant: PennantColour;
  /** Each ship with its index in the whole fleet, which numbers its outline id. */
  readonly members: readonly (readonly [PlacedShip, number])[];
}

/*
 * A masted ship's pennants stream with the wind in its own frame and its set yards take its own
 * brace. Ships of one type, sails and anchor whose wind and brace agree to these steps are drawn
 * from one geometry, built for the middle of the step: their pennants stream within 5 degrees of
 * their wind and their yards within 2.5 degrees of their brace, and the painter view draws some
 * 70 ship meshes rather than one for each of 115 masted ships. Ships alike fly one colour, drawn
 * for them in the painted shares, so the colours split no group; a ship whose colour the painting
 * shows flies that one, in a group of its own where the others' differs. A ship that fires a
 * salute keeps a geometry of its own, for its smoke is its own, but its rig takes the same steps,
 * so it is built from the hull, rig and pennants of the ships alike. Boats without masts share
 * one geometry per type, anchor and timber finish; differing lengths and beams are instanced.
 */
const WIND_STEP = (10 * Math.PI) / 180;
const BRACE_STEP = (5 * Math.PI) / 180;
const TIERS: readonly ShipTier[] = ['far', 'mid', 'near'];

function windAngle(rig: ShipRig): number {
  return Math.atan2(-rig.downwind[2], rig.downwind[0]);
}

function steppedRig(rig: ShipRig): ShipRig {
  const wind = Math.round(windAngle(rig) / WIND_STEP) * WIND_STEP;
  return {
    sailState: rig.sailState,
    brace: Math.round(rig.brace / BRACE_STEP) * BRACE_STEP,
    downwind: [Math.cos(wind), 0, -Math.sin(wind)],
  };
}

function geometryOf(
  ship: PlacedShip,
  rig: ShipRig
): { readonly key: string; readonly rig: ShipRig } {
  if (SHIP_TEMPLATES[ship.type].masts.length === 0) {
    const key = ship.painterAnchor === 'stern' ? `${ship.type}-moored` : ship.type;
    return { key: ship.boat ? `${key}-${ship.boat.finish}` : key, rig };
  }
  const stepped = steppedRig(rig);
  if (ship.gunSmoke.length > 0) return { key: ship.id, rig: stepped };
  const steps = [stepped.brace / BRACE_STEP, windAngle(stepped) / WIND_STEP].map(Math.round);
  return { key: [ship.type, rig.sailState, ship.painterAnchor, ...steps].join(' '), rig: stepped };
}

function groupShips(ships: readonly PlacedShip[], rng: Rng): Group[] {
  const colours = rng.fork('pennants');
  const groups = ships.reduce((map, ship, index) => {
    const alike = geometryOf(ship, rigFor(ship, rng.fork(ship.id).fork('rig')));
    const pennant =
      PAINTED_PENNANTS[ship.id] ?? pennantColourOf(alike.key, colours.fork(alike.key));
    const split = SHIP_TEMPLATES[ship.type].masts.length > 0 && ship.gunSmoke.length === 0;
    const key = split ? `${alike.key} ${pennant}` : alike.key;
    const group = map.get(key) ?? { key, rig: alike.rig, pennant, members: [] };
    return map.set(key, { ...group, members: [...group.members, [ship, index] as const] });
  }, new Map<string, Group>());
  return [...groups.values()];
}

/* What every ship mesh of a stage builds from: its fleet's parts, material and clouds. */
interface Stage {
  readonly parts: ShipParts;
  readonly boatParts: ReadonlyMap<string, ShipParts>;
  readonly material: Material;
  readonly clouds: ReadonlyMap<string, readonly SurfaceMesh[]>;
  readonly matrix: Matrix4;
}

/* Every instance of a type grows by that type's painter scale about its own origin. */
function instancedMesh(
  geometry: BufferGeometry,
  material: Material,
  capacity: number,
  name: string,
  type: ShipType
): InstancedMesh {
  const scales = Array.from({ length: capacity }, () => painterScaleOf(type)).flat();
  geometry.setAttribute(
    'aPainterScale',
    new InstancedBufferAttribute(Float32Array.from(scales), 2)
  );
  const mesh = new InstancedMesh(geometry, material, capacity);
  mesh.name = name;
  mesh.frustumCulled = false;
  return mesh;
}

/* A ship at rest on its heading, until the fleet's animator first rocks it. */
function restingAt(ship: PlacedShip, matrix: Matrix4): Matrix4 {
  const [x, y, z] = layoutToWorld(ship.position, floatingHeight(ship));
  return matrix
    .makeRotationY(ship.heading)
    .scale(new Vector3(ship.boat?.lengthScale ?? 1, 1, ship.boat?.beamScale ?? 1))
    .setPosition(x, y, z);
}

/** Open boats draw 0.45–0.55 m of freeboard above the shared water plane. Their shallow keels must
 * dip into it rather than hover above it; the larger fitted ships keep their existing datum. */
export function floatingHeight(ship: Pick<PlacedShip, 'type'>): number {
  return isOpenBoat(SHIP_TEMPLATES[ship.type])
    ? WORLD.waterY + (ship.type === 'praam' ? 0.55 : 0.45)
    : 0;
}

function partsFor(ship: PlacedShip, stage: Stage): ShipParts {
  return ship.boat ? stage.boatParts.get(ship.boat.finish)! : stage.parts;
}

function groupMesh(group: Group, groupIndex: number, stage: Stage, rng: Rng): InstancedMesh {
  const [first] = group.members[0]!;
  const { material, matrix } = stage;
  const parts = partsFor(first, stage);
  const geometry = buildShipGeometry(
    first.type,
    group.rig,
    group.pennant,
    parts.colors,
    edgeIdFor(groupIndex),
    rng.next(),
    stage.clouds.get(first.id) ?? [],
    first.painterAnchor,
    parts
  );
  const mesh = instancedMesh(geometry, material, group.members.length, group.key, first.type);
  mesh.userData['ships'] = group.members.map(([ship]) => ship.id);
  group.members.forEach(([ship], i) => mesh.setMatrixAt(i, restingAt(ship, matrix)));
  mesh.instanceMatrix.needsUpdate = true;
  const edgeIds = group.members.map(([, index]) => vesselEdgeIdFor(index));
  geometry.setAttribute(
    'aInstanceEdgeId',
    new InstancedBufferAttribute(Float32Array.from(edgeIds), 1)
  );
  return mesh;
}

/* The key of the body a formed ship's hull and standing rig, or an open boat's detail, are drawn
   from. */
function bodyKey(ship: PlacedShip): string | null {
  const template = SHIP_TEMPLATES[ship.type];
  return isFormed(template) || isOpenBoat(template)
    ? `${ship.type} ${ship.painterAnchor}${ship.boat ? ` ${ship.boat.finish}` : ''}`
    : null;
}

/* An open boat's group draws its closed hull far and in the painter view; its body draws the
   rest (openBoat.ts). */
function bodyTiers(type: ShipType): readonly ShipTier[] {
  return isOpenBoat(SHIP_TEMPLATES[type]) ? ['mid', 'near'] : TIERS;
}

/* One instanced body per tier for the formed ships or open boats of one type and anchor, each
   with room for all of them; the fleet's animator fills them. */
function bodyOf(ships: readonly PlacedShip[], id: number, stage: Stage, seed: number): FleetBody {
  const [first] = ships;
  const mesh = (tier: ShipTier): InstancedMesh => {
    const geometry = buildBodyGeometry(
      first!.type,
      tier,
      edgeIdFor(id),
      seed,
      first!.painterAnchor,
      partsFor(first!, stage)
    );
    const ids = new InstancedBufferAttribute(new Float32Array(ships.length), 1).setUsage(
      DynamicDrawUsage
    );
    geometry.setAttribute('aInstanceEdgeId', ids);
    const body = instancedMesh(
      geometry,
      stage.material,
      ships.length,
      `${bodyKey(first!)} ${tier}`,
      first!.type
    );
    body.instanceMatrix.setUsage(DynamicDrawUsage);
    body.userData['shipBody'] = tier;
    body.count = 0;
    body.visible = false;
    return body;
  };
  return { meshes: Object.fromEntries(bodyTiers(first!.type).map((tier) => [tier, mesh(tier)])) };
}

/** Where a ship of the fleet lies and how it rocks: its meshes and its lantern share the pose. */
export function shipInstanceOf(ship: PlacedShip, index: number, rng: Rng): ShipInstance {
  const [x, y, z] = layoutToWorld(ship.position, floatingHeight(ship));
  const motion = generateMotionParams(rng.fork(ship.id));
  // On sheltered canal water the light boats bob gently, keeping the open bottom boards
  // above the water plane while their shallow keels remain immersed.
  const motionParams = isOpenBoat(SHIP_TEMPLATES[ship.type])
    ? { ...motion, bobAmplitude: 0.02 }
    : motion;
  return {
    index,
    baseX: x,
    baseY: y,
    baseZ: z,
    heading: ship.heading,
    motionParams,
    ...(ship.boat ? { lengthScale: ship.boat.lengthScale, beamScale: ship.boat.beamScale } : {}),
  };
}

/* A salute is the painted moment: a ship's smoke, last in its geometry, is drawn while the painter
   view shows and left out of the draw everywhere else. */
function saluteInView(geometry: BufferGeometry, painterAmount: number): void {
  const smokeFrom = geometry.userData[SMOKE_FROM] as number | undefined;
  if (smokeFrom === undefined) return;
  geometry.setDrawRange(0, painterAmount > 0 ? Infinity : smokeFrom);
}

function painterAmountOf(material: Material): () => number {
  const uniform = (material as ShaderMaterial).uniforms?.['uPainterAmount'];
  return () => (uniform?.value as number | undefined) ?? 0;
}

/** Instanced meshes for the placed fleet: a mesh per group of ships alike and a body per tier. */
export function createShipMeshes(
  ships: readonly PlacedShip[],
  colors: ShipColors,
  material: Material,
  rng: Rng
): Fleet {
  const stage: Stage = {
    parts: createShipParts(colors),
    boatParts: new Map(
      (['tarred', 'weathered', 'oak'] as const).map((finish) => [
        finish,
        createShipParts(boatColors(colors, finish)),
      ])
    ),
    material,
    clouds: fleetClouds(ships, rng),
    matrix: new Matrix4(),
  };
  const groups = groupShips(ships, rng);
  const groupMeshes = groups.map((group, g) => groupMesh(group, g, stage, rng));
  const keys = [...new Set(ships.map(bodyKey).filter((key): key is string => key !== null))];
  const seeds = rng.fork('bodies');
  const bodies = keys.map((key, b) =>
    bodyOf(
      ships.filter((ship) => bodyKey(ship) === key),
      groups.length + b,
      stage,
      seeds.next()
    )
  );
  const slots = new Map(
    groups.flatMap((group, g) =>
      group.members.map(([, index], slot) => [index, [g, slot]] as const)
    )
  );
  const fleet = ships.map((ship, index): FleetShip => {
    const [group, groupSlot] = slots.get(index)!;
    const key = bodyKey(ship);
    return {
      instance: shipInstanceOf(ship, index, rng),
      edgeId: vesselEdgeIdFor(index),
      group,
      groupSlot,
      body: key === null ? -1 : keys.indexOf(key),
    };
  });
  const lod = createFleetLod(fleet, groupMeshes, bodies, painterAmountOf(material));
  const meshes = [
    ...groupMeshes,
    ...bodies.flatMap((body) => TIERS.flatMap((tier) => body.meshes[tier] ?? [])),
  ];
  const painterAmount = painterAmountOf(material);
  for (const mesh of meshes)
    mesh.onBeforeRender = (_renderer, _scene, camera) => {
      lod.sense(camera);
      saluteInView(mesh.geometry, painterAmount());
    };
  return { meshes, animated: [lod.animated] };
}
